import { CartAddEvent, CartErrorEvent } from '@theme/events';

class CroBuyBox extends HTMLElement {
  #form = null;
  #variantIdInput = null;
  #sellingPlanInput = null;
  #submitButtons = [];
  #errorEl = null;
  #planRadios = [];
  #lastClickedSubmitter = null;

  connectedCallback() {
    this.#form = this.querySelector('form.cro-buy-box__form');
    if (!this.#form) return;

    this.#variantIdInput = this.#form.querySelector('input[name="id"]');
    this.#sellingPlanInput = this.#form.querySelector('input[name="selling_plan"]');
    this.#submitButtons = this.#form.querySelectorAll('.cro-buy-box__atc');
    this.#errorEl = this.#form.querySelector('.cro-buy-box__error');
    this.#planRadios = this.#form.querySelectorAll('input[name="cro-plan"]');

    this.#planRadios.forEach((radio) => radio.addEventListener('change', this.#onPlanChange));
    this.#form.addEventListener('click', this.#onFormClick);
    this.#form.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#planRadios.forEach((radio) => radio.removeEventListener('change', this.#onPlanChange));
    this.#form?.removeEventListener('click', this.#onFormClick);
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  #onPlanChange = (event) => {
    const radio = (event.target);
    if (!radio.checked) return;

    const variantId = radio.dataset.variantId;
    const sellingPlanId = radio.dataset.sellingPlanId;

    if (variantId && this.#variantIdInput) this.#variantIdInput.value = variantId;
    if (this.#sellingPlanInput) this.#sellingPlanInput.value = sellingPlanId || '';
  };

  #onFormClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('button[type="submit"]') : null;
    if (target instanceof HTMLButtonElement) this.#lastClickedSubmitter = target;
  };

  #onSubmit = async (event) => {
    event.preventDefault();
    if (!this.#form || !this.#variantIdInput) return;

    const submitter =
      event instanceof SubmitEvent && event.submitter instanceof HTMLButtonElement
        ? event.submitter
        : this.#lastClickedSubmitter;
    if (submitter && submitter.dataset.variantId) {
      this.#variantIdInput.value = submitter.dataset.variantId;
      if (this.#sellingPlanInput) this.#sellingPlanInput.value = submitter.dataset.sellingPlanId || '';
    }

    const id = this.#variantIdInput.value;
    const sellingPlanRaw = this.#sellingPlanInput?.value?.trim();

    this.#setLoading(true);
    this.#clearError();

    const item = { id, quantity: 1 };
    if (sellingPlanRaw) item.selling_plan = parseInt(sellingPlanRaw, 10);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const body = {
      items: [item],
      sections: sectionIds.join(','),
    };

    try {
      const response = await fetch(`${Theme.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok && response.status !== 422) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json().catch(() => {
        throw new Error('Invalid JSON response from server');
      });

      if (data.status) {
        const message = data.message || data.description || 'Could not add this item to your cart.';
        this.#showError(message);
        window.dispatchEvent(new CartErrorEvent(this.id, message));

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'cro-buy-box',
            itemCount: 1,
            productId: this.dataset.productId,
          })
        );
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(data, id.toString(), {
          source: 'cro-buy-box',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:added', {
          detail: {
            productId: this.dataset.productId,
            productHandle: this.dataset.productHandle || window.location.pathname.split('/').pop(),
            variantId: id.toString(),
            quantity: 1,
            items: data.items,
            isUpsell: false,
          },
        })
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add item to cart. Please try again.';
      this.#showError(message);
      window.dispatchEvent(new CartErrorEvent(this.id, message));
    } finally {
      this.#setLoading(false);
    }
  };

  #setLoading(isLoading) {
    this.#submitButtons.forEach((button) => {
      button.toggleAttribute('disabled', isLoading);
      button.setAttribute('aria-busy', String(isLoading));
      button.style.opacity = isLoading ? '0.6' : '';
    });
  }

  #showError(message) {
    if (!this.#errorEl) return;
    this.#errorEl.textContent = message;
  }

  #clearError() {
    if (!this.#errorEl) return;
    this.#errorEl.textContent = '';
  }
}

if (!customElements.get('cro-buy-box')) {
  customElements.define('cro-buy-box', CroBuyBox);
}
